// core/work-fence.ts
//
// THE FENCES, AS PRIMITIVES (documentation/agent-harness.md, step 3). A model
// asks in words: it ends a reply with ONE fenced block — read, do, table,
// write, handoff, continue — and the hive finds the block, turns its lines
// into the canonical grammar and holds a work block back from the visible
// stream. That finding is pure text work, and the `stretch` step (a streamed
// round, core/agent-stretch.ts) needs it, so it lives here where both the
// chat window (shared) and a step bee (essentials) can reach it. The words a
// model is TAUGHT — the instructions and the messages sent back — stay in
// the shared work-fence module, which re-exports everything here.

import { FENCE_RE } from '../question-fence.js'
import { CONTINUE_FENCE_LANG } from './agent-leg.js'

export const READ_FENCE_LANG = 'hypercomb-read'
export const DO_FENCE_LANG = 'hypercomb-do'
export const TABLE_FENCE_LANG = 'hypercomb-table'
/** A module section written back (documentation/hive-read-fence.md, "Writing
 *  a module"): first line `<bee signature> <src/path.ts>`, the rest the
 *  section's new body. The hive drafts the module and runs it here. */
export const WRITE_FENCE_LANG = 'hypercomb-write'

/** Rounds one participant message may take before the model must answer. */
/** HANDING OFF. A model that cannot do the work says so in this fence, one
 *  line, and the turn goes to another model instead of an apology
 *  (jwize, 2026-09-24: "have one of the subscriptions take care of that").
 *  The provider that wrote it is not asked again this turn. */
export const HANDOFF_FENCE_LANG = 'hypercomb-handoff'
/** How many times one turn may change hands before it stops. */
export const MAX_HANDOFFS = 2

export type WorkKind = 'read' | 'do' | 'table' | 'write'

export type WorkRequest = {
  readonly kind: WorkKind
  /** Canonical slash grammar, in the order the model wrote it. */
  readonly lines: readonly string[]
}

export type SplitWork = {
  /** The reply with every work block removed. */
  readonly prose: string
  /** What to run this round. Reads win when one reply carries both. */
  readonly request?: WorkRequest
  /** A do block rode alongside a read block and was not run. */
  readonly heldDo?: true
  /** The model gave the work up, with its reason. Wins over any request. */
  readonly handoff?: string
  /** The model handed the work to its next leg: what is left, one line. */
  readonly left?: string
  /** The reply NAMES a work block inside markup and carries none that can be
   *  run: the block's word, so the loop can send it back to be written. */
  readonly unwritten?: string
}

type BlockKind = WorkKind | 'handoff' | 'continue'

type Block = {
  readonly kind: BlockKind
  readonly open: number
  /** Index of the closing fence line, or `lines.length` when never closed. */
  readonly end: number
  readonly body: readonly string[]
}

/**
 * Some smaller models put the requested info string on the first line of a
 * generic text fence instead of on the opening fence itself:
 *
 * ```text
 * hypercomb-read
 * read /somewhere
 * ```
 *
 * The literal marker is still an unambiguous opt-in to the work protocol.
 * Accept only that exact first non-empty line; never infer work from an
 * unlabeled block containing ordinary slash-looking examples.
 */
const markedBody = (info: string, body: readonly string[]):
  { readonly kind: BlockKind; readonly body: readonly string[] } | null => {
  if (kindOf(info)) return null
  const first = body.findIndex(line => line.trim().length > 0)
  if (first < 0) return null
  const kind = kindOf(body[first])
  if (!kind) return null
  return { kind, body: body.slice(first + 1) }
}

/** A table is unmistakable by shape: a JSON object whose first key is rows.
 *  Models that were told to write a hypercomb-table fence still reach for a
 *  json fence, or no fence; the shape is the opt-in either way. */
const looksLikeTable = (body: readonly string[]): boolean =>
  /^\{\s*"rows"\s*:/.test(body.map(line => line.trim()).filter(Boolean).join(''))

const kindOf = (info: string): BlockKind | null => {
  const word = info.trim().split(/\s+/)[0] ?? ''
  if (word === HANDOFF_FENCE_LANG) return 'handoff'
  if (word === CONTINUE_FENCE_LANG) return 'continue'
  if (word === READ_FENCE_LANG) return 'read'
  if (word === DO_FENCE_LANG) return 'do'
  if (word === TABLE_FENCE_LANG) return 'table'
  if (word === WRITE_FENCE_LANG) return 'write'
  return null
}

const closes = (line: string, run: string): boolean => {
  const match = FENCE_RE.exec(line)
  return !!match && match[1][0] === run[0] && match[1].length >= run.length && !match[2].trim()
}

/**
 * THE TAG SPELLING. Some models write the block as a tag instead of a fence:
 *
 *   <hypercomb-read> read / </hypercomb-read>
 *
 * The tag name is the same literal marker the fence carries, so it is the
 * same unambiguous opt-in — and a reply that carries it and is not run ends
 * the turn with the machinery showing and nothing read (jwize's drive test,
 * 2026-09-30). Only the exact work words open one; any other tag is prose.
 *
 * It may open anywhere on a line outside a code fence — what stands before it
 * stays prose — but a model also MENTIONS the tag in a sentence, so only two
 * shapes are a block: the BLOCK form (nothing after the opener on its line,
 * the body on the lines below, the closer later; never closed still counts,
 * as an unclosed fence does) and the INLINE form (the closer on the opener's
 * own line). An opener followed by words and no closer on that line is a
 * sentence about the tag, and so is one written inside inline code.
 */
const TAG_STEM = '<hypercomb-'
const TAG_OPEN_RE = /^<(hypercomb-[a-z]+)>/
/** The closer as a model spells it: any case, spaces inside the brackets. */
const tagCloser = (name: string): RegExp => new RegExp(`<\\s*/\\s*${name}\\s*>`, 'i')
/** Inside inline code: an odd number of ticks before it on the line. */
const inCode = (lead: string): boolean => lead.split('`').length % 2 === 0

type Tag = {
  readonly kind: BlockKind
  /** What stands before the opener on its line. It stays prose. */
  readonly lead: string
  /** What follows the opener on its line. */
  readonly after: string
  readonly closer: RegExp
}

/** The first work tag a line opens, in one of the two shapes. */
const tagAt = (line: string): Tag | null => {
  for (let at = line.indexOf(TAG_STEM); at >= 0; at = line.indexOf(TAG_STEM, at + 1)) {
    const open = TAG_OPEN_RE.exec(line.slice(at))
    const kind = open ? kindOf(open[1]) : null
    if (!open || !kind) continue
    const lead = line.slice(0, at)
    if (inCode(lead)) continue
    const after = line.slice(at + open[0].length)
    const closer = tagCloser(open[1])
    if (after.trim() && !closer.test(after)) continue
    return { kind, lead, after, closer }
  }
  return null
}

/**
 * A model mixing the two spellings wraps a fence in the tag. The outer fence
 * pair is wrapping, not body: left in, its lines are read as commands and the
 * whole block is refused. Only that pair goes; the rest stays as written.
 */
const unfenced = (body: readonly string[]): readonly string[] => {
  const first = body.findIndex(line => line.trim().length > 0)
  const fence = first < 0 ? null : FENCE_RE.exec(body[first])
  if (!fence) return body
  let last = body.length - 1
  while (last > first && !body[last].trim()) last--
  return body.slice(first + 1, last > first && closes(body[last], fence[1]) ? last : undefined)
}

/**
 * The tag block a line opens. The lines are the scanner's own and are re-cut
 * here, so the words around a block survive it: what stands before the opener
 * becomes its own line above, and what follows the closer its own line below
 * — where the scanner reads it next, as prose or as a further block.
 */
const tagBlock = (lines: string[], index: number): Block | null => {
  const tag = tagAt(lines[index])
  if (!tag) return null
  if (tag.lead.trim()) lines.splice(index++, 0, tag.lead.trimEnd())
  const body: string[] = []
  let rest = tag.after
  let end = index
  for (;;) {
    const close = tag.closer.exec(rest)
    if (close) {
      if (rest.slice(0, close.index).trim()) body.push(rest.slice(0, close.index))
      const tail = rest.slice(close.index + close[0].length).trimStart()
      if (tail) lines.splice(end + 1, 0, tail)
      break
    }
    // Interior lines are kept as written, blank ones too, exactly as the
    // fence path keeps them: a write block is code and a doctrine write is
    // markdown. Only the empty remainder of the opener's line falls away.
    if (end > index) body.push(rest)
    end++
    if (end >= lines.length) break
    rest = lines[end]
  }
  return { kind: tag.kind, open: index, end, body: unfenced(body) }
}

/**
 * THE MARKER LINE. A model wrote the info string as a line of its own, in
 * inline code, with no fence at all (jwize's drive session, 2026-10-01):
 *
 *   `hypercomb-read`
 *   code bubble engine
 *
 * The marker alone on its line is the same literal opt-in the fence carries;
 * its block is the lines under it, up to the first blank line after them.
 */
const MARKER_LINE = /^\s*`?(hypercomb-[a-z]+)`?\s*:?\s*$/

const markerBlock = (lines: readonly string[], index: number): Block | null => {
  const marker = MARKER_LINE.exec(lines[index])
  const kind = marker ? kindOf(marker[1]) : null
  if (!kind) return null
  let end = index
  while (end + 1 < lines.length && lines[end + 1].trim() && !FENCE_RE.test(lines[end + 1])) end++
  if (end === index) return null
  return { kind, open: index, end, body: lines.slice(index + 1, end + 1) }
}

/**
 * THE GLUED FENCE. A model wrote the opening fence at the END of a sentence,
 * on the sentence's own line (live probe, 2026-10-12):
 *
 *   I'll find practice-modules.```hypercomb-read
 *   find practice-modules
 *   ```
 *
 * A fence opens only at the start of a line (FENCE_RE), so the block showed as
 * text and never ran — reads 0. When the backticks carry a WORK word and
 * nothing after it, that is the same literal opt-in the fence on its own line
 * carries: the words before the backticks are prose, and the fence opens
 * there. Only work words — an ordinary fence glued to text (```ts) stays
 * text, as before — and never inside inline code, nor with no words before
 * it (an indented fence is markdown's, and FENCE_RE weighs it).
 *
 * A sentence also ENDS by naming the fence — "I open a block with
 * ```hypercomb-read" — and then goes on in prose. So the glued fence opens
 * only onto a body: the line under it is words (bodyBelow). A blank line, a
 * fence or the end of the reply under it leaves the sentence whole (review of
 * the 2026-10-12 fix: opened anyway, it swallowed the explanation after it as
 * a read).
 *
 * The match starts at the FIRST backtick of its run — `(?<!`)` — or a long
 * run is retried from every backtick in it (same review).
 */
// `[ \t]*$`, not `\s*$`: a CR left on the line is no fence end (FENCE_RE's
// own `(.*)$` refuses it too), so CRLF replies agree with the ordinary fence.
const GLUED_FENCE_RE = /(?<!`)(`{3,})\s*(hypercomb-[a-z]+)[ \t]*$/

const gluedFence = (line: string): { readonly lead: string; readonly fence: string } | null => {
  const glued = GLUED_FENCE_RE.exec(line)
  if (!glued || !kindOf(glued[2])) return null
  const lead = line.slice(0, glued.index)
  // Words that are themselves a fence opener are never prose before a glue.
  if (!lead.trim() || inCode(lead) || FENCE_RE.test(lead)) return null
  return { lead, fence: `${glued[1]}${glued[2]}` }
}

/** The line under a glued fence opens it: words — not a blank line, not a
 *  fence, not the end of the reply. */
const bodyBelow = (line: string | undefined): boolean => !!line?.trim() && !FENCE_RE.test(line)

/** Every work block, skipping the inside of any other fence. A tag block
 *  re-cuts `lines` (see tagBlock); the indices are into the array as left. */
const scan = (lines: string[]): Block[] => {
  const blocks: Block[] = []
  let index = 0
  while (index < lines.length) {
    const open = FENCE_RE.exec(lines[index])
    if (!open) {
      // A glued fence over a body re-cuts the line in two: the words, read
      // again here as prose (or a tag), and the fence on its own line below,
      // read next.
      const glued = gluedFence(lines[index])
      if (glued && bodyBelow(lines[index + 1])) { lines.splice(index, 1, glued.lead.trimEnd(), glued.fence); continue }
      const tagged = tagBlock(lines, index) ?? markerBlock(lines, index)
      if (tagged) { blocks.push(tagged); index = tagged.end + 1 } else index++
      continue
    }
    let end = index + 1
    while (end < lines.length && !closes(lines[end], open[1])) end++
    const body = lines.slice(index + 1, end)
    const marked = markedBody(open[2], body)
    const kind = kindOf(open[2]) ?? marked?.kind ?? (looksLikeTable(body) ? 'table' : null)
    // An unclosed work block still counts: a model that stopped generating
    // before the closer meant the request all the same.
    if (kind) blocks.push({ kind, open: index, end, body: marked?.body ?? body })
    index = end + 1
  }
  return blocks
}

/**
 * One line of a block → the canonical grammar the parsers read. List markers
 * and inline-code ticks fall away, the leading slash is optional, the verb is
 * lower-cased, and for READS the word `here` names the current page, which the
 * observation parser spells as a bare verb.
 */
export const workLineGrammar = (raw: string, kind: WorkKind): string => {
  // A table is JSON and a write is code: both are taken as written.
  if (kind === 'table' || kind === 'write') return raw
  let line = String(raw ?? '').trim()
    .replace(/^(?:[-*•]|\d+[.)])\s+/, '')
    .replace(/^`(.*)`$/, '$1')
    .trim()
    .replace(/^\//, '')
    .replace(/^[A-Za-z][\w-]*/, verb => verb.toLowerCase())
  if (!line) return ''
  if (kind === 'read') {
    if (/^[0-9a-f]{64}$/i.test(line)) return `/read ${line.toLowerCase()}`
    line = line.replace(/^([a-z][a-z0-9-]*)\s+here$/, '$1')
    // Small models sometimes treat the code-discovery grammar as prose and
    // say `read code core`. That spelling cannot name a tile (routes require
    // a leading slash), so correcting it to `code core` is unambiguous. Keep
    // `read /code` literal: that really does mean the tile at /code.
    line = line.replace(/^read\s+code(?:\s+(.+))?$/i, (_whole, query: string | undefined) =>
      query?.trim() ? `code ${query.trim()}` : 'code')
  }
  return `/${line}`
}

/**
 * A BLOCK NAMED AND NOT WRITTEN. The fence, the tag, the info string on a
 * text fence's first line — each spelling was learned from a turn that ended
 * with the machinery on the screen and nothing done, and a model will find a
 * fourth: `<block info="hypercomb-read">` came next (jwize's drive session,
 * 2026-09-30). Rather than learn spellings one dead turn at a time, a reply
 * that names a work block's word INSIDE MARKUP — angle brackets — and carries
 * no block the hive can run says which word it was, so the loop sends it back
 * to be written properly. A sentence that merely mentions the word, a word in
 * inline code, and anything inside an ordinary code fence are not markup and
 * say nothing. Nor does the hive's own tag (`<hypercomb-read>`): the scanner
 * above already weighed that one, and an opener it left as prose is a
 * sentence about the tag — only a word carried by SOME OTHER markup is the
 * unread spelling this is for.
 */
const NAMED_IN_MARKUP = /<(?!\s*\/?\s*hypercomb-)[^<>\n]*\b(hypercomb-(?:read|do|table|write|handoff|continue))\b[^<>\n]*>/g

/**
 * ...AND WORK WRITTEN WITH NO BLOCK AT ALL. A reply that was nothing but
 * `/file on /bubble-bobble-dos-v1: …` lines, bare, ended as "answered" with
 * six notes owed and none filed (jwize's drive session, 2026-10-01). A line
 * that opens with a slash, a word, and a space is a command line the hive
 * would run — a route never has the space (`/games/bubble is …` reads as a
 * route) — so two or more of them outside any fence are a hypercomb-do block
 * that was never opened. One alone may be a sentence about a command.
 */
const BARE_COMMAND = /^\s*\/[a-z][a-z-]*\s+\S/
const BARE_COMMANDS_MIN = 2
/** A REPLY THAT IS ONLY A READ: `read 33618262568d src/…` and nothing else,
 *  answered and done with nothing read (the same session). The read words
 *  are written without a slash inside their block, so a whole reply of them
 *  is a read block never opened. */
const BARE_READ = /^\s*\/?(?:read|tree|code)\s+\S/

/** A SHORT REPLY THAT ONLY PROMISES. "I'll read the module and both sections
 *  first." and nothing after it — the turn ended answered, nothing read (the
 *  same session; an open item since 09-30). A brief reply whose last line
 *  announces the next step is that step's block, never written. */
const PROMISE = /^(?:(?:ok(?:ay)?|first|now|next|then)[,:]?\s+)*(?:i(?:'|’)ll|i will|let me|i(?:'|’)m going to|i am going to)\s+(.+)$/i
const READ_STEP = /\b(?:read|look|check|open|search|find|inspect|examine|review|list)\b/i
const PROMISE_MAX = 400

const promisedIn = (said: readonly string[]): string | undefined => {
  if (!said.length || said.join('\n').length > PROMISE_MAX) return undefined
  const step = PROMISE.exec(said[said.length - 1])
  if (!step) return undefined
  return READ_STEP.test(step[1]) ? 'hypercomb-read' : 'hypercomb-do'
}

/** A FENCE OF NOTHING BUT COMMANDS, the fence untagged or called a shell.
 *  Asked to "go ahead and do those moves", a hive model answered with a fenced
 *  `/into …` line, once alone and once under "here is the move block, ready to
 *  run" (the first `say` conversation, 2026-10-25): the fence is the ordinary
 *  kind, so the scanner left it as prose and the turn ended answered with
 *  nothing done. This only SENDS THE REPLY BACK — once, with "if you were only
 *  describing the block, give the answer again without it" — so a fence that
 *  was an example costs a round and runs nothing. A fence that mixes commands
 *  with other lines, or is in another language, is code and is left alone. */
const hasFencedCommands = (lines: readonly string[]): boolean => {
  for (let at = 0; at < lines.length; at++) {
    const open = FENCE_RE.exec(lines[at])
    if (!open || !/^(?:|text|txt|sh|bash|shell|console)$/i.test(open[2].trim())) continue
    let end = at + 1
    while (end < lines.length && !closes(lines[end], open[1])) end++
    const body = lines.slice(at + 1, end).map(line => line.trim()).filter(Boolean)
    if (end < lines.length && body.length > 0 && body.every(line => BARE_COMMAND.test(line))) return true
    at = end
  }
  return false
}

const unwrittenIn = (lines: readonly string[]): string | undefined => {
  const said = lines.map(line => line.trim()).filter(Boolean)
  if (said.length && said.length <= 8 && said.every(line => BARE_READ.test(line))) return 'hypercomb-read'
  if (hasFencedCommands(lines)) return 'hypercomb-do'
  const promised = promisedIn(said)
  if (promised) return promised
  let fence: string | null = null
  let bare = 0
  for (const line of lines) {
    const mark = FENCE_RE.exec(line)
    if (mark) {
      if (!fence) fence = mark[1]
      else if (closes(line, fence)) fence = null
      continue
    }
    if (fence) continue
    for (const named of line.matchAll(NAMED_IN_MARKUP)) {
      if (!inCode(line.slice(0, named.index))) return named[1]
    }
    if (BARE_COMMAND.test(line)) bare += 1
  }
  return bare >= BARE_COMMANDS_MIN ? 'hypercomb-do' : undefined
}

// ── A TURN THAT ENDS ON A PROMISE (documentation/conversation-continuity.md) ──
//
// jwize, 2026-10-24: "sometimes the conversations just say they are going to
// wait or do something but they are finished already." The check above reads a
// SHORT reply's LAST line for four openers and sends it back once; a longer
// answer that closes "Next, I'll update the other tiles." ended the work with
// nothing to carry it on. This reads how ANY reply ends — the last paragraph's
// closing lines — so the turn's end can record the promise as work still owed,
// and tells "I'll wait for your go-ahead" (waiting on the participant) from
// "I'll wait for the walk to finish" (nothing will).

const PROMISE_OPENER = new RegExp(
  '^[\\s*_>•-]*(?:(?:ok(?:ay)?|alright|all right|so|great|good|first|now|next|then|finally)[,:]?\\s+)*'
  + '(?:\\*{1,2}next:?\\*{1,2}:?\\s*)?'
  + '(?:i(?:\'|’)ll|i will|let me|i(?:\'|’)m going to|i am going to|i(?:\'|’)m about to|i am about to|'
  + 'we(?:\'|’)ll|we will|i still need to|i(?:\'|’)ll now|next,?\\s+i(?:\'|’)ll|now i(?:\'|’)ll)\\s+(.+)$',
  'i',
)
const AFTER_OPENER = /^[\s*_>•-]*(?:once|after|when|as soon as)\b[^.!?]*?,\s*(?:i(?:'|’)ll|i will|we(?:'|’)ll)\s+(.+)$/i
/** Sign-offs, negations and closers that read like promises and owe nothing. */
const NOT_A_PROMISE = /^(?:you know\b|be here\b|be (?:happy|glad)\b|gladly\b|leave\b|stop here\b|keep (?:an eye|that in mind|it in mind)\b|remember\b|note that\b|not\b|never\b|be waiting\b|see\b|answer\b|summari[sz]e\b|explain\b|do (?:whichever|whatever) you\b)/i
/** "Let me know …" ends the reading: it asks the participant, and whatever the
 *  lines above it say is not owed while it stands. */
const ASKS_YOU = /^(?:me )?know\b/i
/** A wait on the participant, not a promise of work. Errs toward waiting: a
 *  wrong wait costs the participant one word; a wrong promise sends "Continue"
 *  on their behalf, which a model may take for the go-ahead it asked for
 *  (phase-1 review, 2026-10-24). */
const WAITS_ON_YOU = new RegExp(
  '\\b(?:a?wait(?:ing)?|hold(?:ing)? off|pause|stop)\\b[^.!?]*\\b(?:you|your)\\b'
  + '|\\b(?:once|when|after|as soon as|until|unless|before|so|so that)\\s+(?:you|confirmed|approved)\\b'
  + '|\\bif you\\s+(?:confirm|approve|agree|say|want|would like|\'d like|’d like)\\b'
  + '|\\b(?:need|require)\\s+(?:you|your)\\b'
  + '|\\byour\\s+(?:ok|okay|go-ahead|approval|confirmation|permission|answer|input|decision|choice|reply|pick)\\b'
  + '|\\b(?:confirmation|approval|go-ahead)\\b'
  + '|\\b(?:after|on|with)\\s+your\\b',
  'i',
)
/** Closing lines read, bottom up, of the last paragraph. */
const ENDING_LINES = 3

export type TurnEnding =
  | { readonly kind: 'promise'; readonly step: string }
  | { readonly kind: 'waiting'; readonly step: string }

/** How a reply ends: a promise of work, a wait on the participant, or neither
 *  (undefined). Reads the last paragraph's closing lines, outside any fence. Pure. */
export const turnEnding = (prose: string): TurnEnding | undefined => {
  const lines = String(prose ?? '').replace(/\s+$/, '').split('\n')
  const kept: string[] = []
  let fence: string | null = null
  for (const line of lines) {
    const mark = FENCE_RE.exec(line)
    if (mark) {
      if (!fence) fence = mark[1]
      else if (closes(line, fence)) fence = null
      kept.push('')
      continue
    }
    kept.push(fence ? '' : line)
  }
  const blank = kept.map(line => line.trim()).lastIndexOf('', kept.length - 1)
  const paragraph = kept.slice(blank + 1).map(line => line.trim()).filter(Boolean)
  for (const line of paragraph.slice(-ENDING_LINES).reverse()) {
    const said = PROMISE_OPENER.exec(line) ?? AFTER_OPENER.exec(line)
    if (!said) continue
    const step = said[1].trim()
    if (ASKS_YOU.test(step)) return undefined
    // A line that introduces what follows ("Let me summarize:") promises nothing.
    if (NOT_A_PROMISE.test(step) || /:\s*$/.test(step)) continue
    if (WAITS_ON_YOU.test(line)) return { kind: 'waiting', step }
    return { kind: 'promise', step }
  }
  return undefined
}

/** Split a finished round into what the model said and what it asked for. */
export const splitWork = (text: string): SplitWork => {
  const lines = String(text ?? '').split('\n')
  const blocks = scan(lines)
  if (!blocks.length) {
    // The whole reply is the table, with no fence at all.
    if (looksLikeTable(lines)) return { prose: '', request: { kind: 'table', lines } }
    const unwritten = unwrittenIn(lines)
    return { prose: String(text ?? ''), ...(unwritten ? { unwritten } : {}) }
  }
  const removed = new Set<number>()
  for (const block of blocks) {
    for (let at = block.open; at <= Math.min(block.end, lines.length - 1); at++) removed.add(at)
  }
  const prose = lines.filter((_, at) => !removed.has(at)).join('\n').replace(/\n{3,}/g, '\n\n').trim()
  const oneLine = (block: Block, fallback: string): string =>
    block.body.map(line => line.trim()).filter(Boolean).join(' ').slice(0, 300) || fallback
  const handoff = blocks.find(block => block.kind === 'handoff')
  if (handoff) return { prose, handoff: oneLine(handoff, 'no reason given') }
  const next = blocks.find(block => block.kind === 'continue')
  const left = next ? { left: oneLine(next, 'the rest of the request') } : {}
  const linesOf = (kind: WorkKind): string[] => blocks
    .filter(block => block.kind === kind)
    .flatMap(block => block.body)
    .map(line => workLineGrammar(line, kind))
    .filter(Boolean)
  const reads = linesOf('read')
  const changes = linesOf('do')
  const tables = blocks.filter(block => block.kind === 'table')
  // A WRITE IS ONE BLOCK, KEPT WHOLE: its lines are code, and a block the
  // model never closed is not code the hive should run.
  const writes = blocks.filter(block => block.kind === 'write')
  const held = changes.length || tables.length || writes.length ? { heldDo: true as const } : {}
  if (reads.length) return { prose, request: { kind: 'read', lines: reads }, ...held, ...left }
  if (writes.length) {
    const valid = writes.length === 1 && writes[0].end < lines.length
    return { prose, request: { kind: 'write', lines: valid ? writes[0].body : [] }, ...(changes.length || tables.length ? { heldDo: true as const } : {}), ...left }
  }
  if (tables.length) {
    const valid = tables.length === 1 && tables[0].end < lines.length
    return { prose, request: { kind: 'table', lines: valid ? tables[0].body : [] }, ...(changes.length ? { heldDo: true as const } : {}), ...left }
  }
  if (changes.length) return { prose, request: { kind: 'do', lines: changes }, ...left }
  return { prose, ...left }
}

/** Could a line that has only begun still turn out to be a fence line? */
const MAY_BE_FENCE = /^\s{0,3}(?:[`~].*)?$/
/** A line so far that could still become `hypercomb-<word>` alone. */
const MAY_BE_MARKER = /^\s*h(?:y(?:p(?:e(?:r(?:c(?:o(?:m(?:b(?:-[a-z]*\s*:?\s*)?)?)?)?)?)?)?)?)?$/
/** …or an opener that is still being written? */
const MAY_BE_TAG = /^<hypercomb-[a-z]*$/
/** …or a line's end that may still become a glued work fence: backticks, then
 *  perhaps the start of `hypercomb-<word>` (gluedFence). It matches from the
 *  first backtick of a run only, as GLUED_FENCE_RE does. */
const MAY_BE_GLUED = /(?<!`)`+\s*(?:h(?:y(?:p(?:e(?:r(?:c(?:o(?:m(?:b(?:-[a-z]*\s*)?)?)?)?)?)?)?)?)?)?$/

/**
 * Where a line that is still arriving must wait from for a glued fence: the
 * backticks ending it. The line's length when nothing has to wait.
 *
 * It is weighed on every character, so it costs only what arrived since the
 * shown point: it looks from there on (a run of backticks cut by that point,
 * from the run's first backtick), and it does not ask whether the backticks
 * close inline code — a run held that did is shown as soon as the line goes
 * past what a glued fence could be, or at the line's end, where gluedFence
 * weighs inline code. Searched over the whole line, with inline code counted
 * each time, a 2000-backtick run took seconds, and so did a 50k-character line
 * of inline code (review of the 2026-10-12 fix: the chat froze while it
 * streamed).
 */
const gluedHold = (line: string, from: number): number => {
  let start = from
  while (start > 0 && line[start - 1] === '`') start--
  const glued = MAY_BE_GLUED.exec(line.slice(start))
  return glued ? Math.max(start + glued.index, from) : line.length
}

/**
 * Where on a finished line a glued fence would open work, or -1 when it has
 * none: at its backticks, or at a work tag its words open — the parser cuts
 * the fence away and reads the words again, so `<hypercomb-read>` alone before
 * it is the BLOCK form, machinery (review of the 2026-10-12 fix: shown, it put
 * the opener in the answer). The words before either are prose.
 */
const gluedAt = (line: string): number => {
  const glued = gluedFence(line)
  if (!glued) return -1
  const lead = tagAt(glued.lead)?.lead.length ?? glued.lead.length
  return line.slice(0, lead).trim() ? lead : 0
}

/**
 * Where a line that is still arriving must wait from: the first `<` at or
 * after `from` that may yet open a work tag — a prefix of the stem, an opener
 * still being written, or a whole work opener, whose shape only the end of
 * its line settles. The line's length when nothing has to wait.
 */
const tagHold = (line: string, from: number): number => {
  for (let at = line.indexOf('<', from); at >= 0; at = line.indexOf('<', at + 1)) {
    const text = line.slice(at)
    const open = TAG_OPEN_RE.exec(text)
    const may = open ? !!kindOf(open[1]) : TAG_STEM.startsWith(text) || MAY_BE_TAG.test(text)
    if (may && !inCode(line.slice(0, at))) return at
  }
  return line.length
}

/**
 * WHAT MAY BE SHOWN WHILE A ROUND STREAMS. Prose goes out as it arrives; from
 * the first work block on, everything is held, because the block is machinery
 * the participant sees in the Execution window instead. A line that has only
 * begun is held while it could still become a fence line, and released the
 * moment it cannot. A tag may open anywhere on a line, so the words before it
 * are shown and the line waits from its `<` on, until it is settled whether
 * that opens work or is a sentence. Ordinary code blocks pass straight
 * through, and nothing inside one is mistaken for a work block.
 */
export class WorkStreamGuard {
  /** The line being written, whole, and how much of it has been shown. */
  #line = ''
  #shown = 0
  #fence: string | null = null
  #holding = false
  /** AN ORDINARY FENCE WAITS FOR ITS FIRST LINE. A model may write the marker
   *  as a plain fence's first line (markedBody), and the hive runs that block
   *  — but shown as it streamed, it led an answer with "```" and
   *  "hypercomb-read" (housekeeping, 2026-10-11). So a fence whose info names
   *  no work holds its opening line (and any blank lines) until the first
   *  body line says whether this is work. */
  #pending = ''
  /** A WORK FENCE GLUED TO A SENTENCE WAITS FOR THE NEXT LINE (gluedFence):
   *  the held end of its line, from the fence (or a tag the words open) on.
   *  A body line under it opens the block, as the parser opens it; anything
   *  else leaves a sentence that names the fence, and it shows whole. */
  #glued = ''

  get holding(): boolean { return this.#holding }

  push(chunk: string): string {
    if (this.#holding) return ''
    let out = ''
    for (const char of chunk) {
      if (char !== '\n') {
        this.#line += char
        const hold = this.#holdFrom()
        out += this.#line.slice(this.#shown, hold)
        this.#shown = hold
        continue
      }
      if (this.#glued) {
        if (bodyBelow(this.#line)) {
          this.#glued = ''
          this.#holding = true
          this.#line = ''
          this.#shown = 0
          return out
        }
        // Not a body: the glued line was a sentence. It shows, and this line
        // is weighed below as any other.
        out += this.#glued
        this.#glued = ''
      }
      if (this.#pending) {
        const line = this.#line
        this.#line = ''
        this.#shown = 0
        if (!line.trim()) { this.#pending += '\n'; continue }
        if (kindOf(line.trim())) {
          this.#pending = ''
          this.#holding = true
          return out
        }
        out += `${this.#pending}${line}\n`
        this.#pending = ''
        if (this.#fence && closes(line, this.#fence)) this.#fence = null
        continue
      }
      const before = this.#fence
      const work = this.#opensWork(this.#line)
      if (work >= 0) {
        out += this.#line.slice(this.#shown, work)
        this.#holding = true
        this.#line = ''
        this.#shown = 0
        return out
      }
      if (!before && this.#fence) {
        this.#pending = `${this.#line}\n`
        this.#line = ''
        this.#shown = 0
        continue
      }
      const glued = this.#fence ? -1 : gluedAt(this.#line)
      if (glued >= 0) {
        const from = Math.max(this.#shown, glued)
        out += this.#line.slice(this.#shown, from)
        this.#glued = `${this.#line.slice(from)}\n`
        this.#line = ''
        this.#shown = 0
        continue
      }
      out += `${this.#line.slice(this.#shown)}\n`
      this.#line = ''
      this.#shown = 0
    }
    return out
  }

  /** The held tail at the end of a round, unless a work block took it. */
  end(): string {
    if (this.#holding) return ''
    const line = this.#line
    const shown = this.#shown
    const glued = this.#glued
    this.#line = ''
    this.#shown = 0
    this.#glued = ''
    // A glued fence whose body line was cut off before its newline still
    // opens, as an unclosed block does; with no body it shows, and the round's
    // last line is weighed after it. (#glued and #pending never both wait.)
    if (glued && bodyBelow(line)) {
      this.#holding = true
      return ''
    }
    if (this.#pending) {
      const pending = this.#pending
      this.#pending = ''
      if (line.trim() && kindOf(line.trim())) {
        this.#holding = true
        return ''
      }
      return `${pending}${line}`
    }
    const work = line ? this.#opensWork(line) : -1
    if (work < 0) return `${glued}${line.slice(shown)}`
    this.#holding = true
    return `${glued}${line.slice(shown, work)}`
  }

  /** How much of the line so far may be shown. */
  #holdFrom(): number {
    const line = this.#line
    // The first line of an ordinary fence waits whole (#pending), and so does
    // the line under a glued fence (#glued).
    if (this.#pending || this.#glued) return this.#shown
    if (MAY_BE_FENCE.test(line)) return 0
    // A line that may yet be a block's marker alone (`hypercomb-read`) waits
    // until it is one or is not.
    if (!this.#fence && MAY_BE_MARKER.test(line)) return this.#shown
    if (this.#fence) return line.length
    // The words before a tag or a glued fence show; the line waits from it.
    // A pending work tag holds first: a glued hold's tail is only backticks,
    // spaces and marker letters and never holds a `<`, so this is the same
    // minimum without rescanning the line on every character (2026-10-12).
    const tag = tagHold(line, this.#shown)
    const at = tag < line.length ? tag : gluedHold(line, this.#shown)
    // An indent is not prose: it waits with the tag it stands before.
    return at < line.length && !line.slice(0, at).trim() ? this.#shown : at
  }

  /** Where on a finished line the held work begins, or -1 when it opens none. */
  #opensWork(line: string): number {
    const match = FENCE_RE.exec(line)
    if (!match) {
      // THE MARKER ALONE ON ITS LINE opens work too (markerBlock): what
      // follows is the block, never prose. Shown, it left "hypercomb-read /
      // tree / list …" at the head of an answer (housekeeping, 2026-10-01).
      if (!this.#fence) {
        const marker = MARKER_LINE.exec(line)
        if (marker && kindOf(marker[1])) return 0
      }
      // The tag spelling opens work too, outside any ordinary code block; the
      // words before it on the line are prose, and are shown.
      const tag = this.#fence ? null : tagAt(line)
      if (!tag) return -1
      return tag.lead.trim() ? tag.lead.length : 0
    }
    if (this.#fence) {
      if (closes(line, this.#fence)) this.#fence = null
      return -1
    }
    if (kindOf(match[2])) return 0
    this.#fence = match[1]
    return -1
  }
}

/** A block the hive would not run, said so the model can correct it. It goes
 *  back to the model as the next message; any other error ends the work. */
